/**
 * GRI AI Executive Commentary
 * AI-powered analysis of GRI reporting readiness
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Brain, TrendingUp, AlertTriangle, CheckCircle, Lightbulb,
  RefreshCw, FileText, BarChart3, Clock, Sparkles
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const GRIAICommentary = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [commentary, setCommentary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (study?.id) {
      fetchCommentary();
    }
  }, [study?.id]);

  const fetchCommentary = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/gri-v2/${study.id}/ai-commentary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommentary(res.data);
    } catch (error) {
      console.error('Error fetching AI commentary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCommentary = async () => {
    setGenerating(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/gri-v2/${study.id}/generate-ai-commentary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommentary(res.data);
      showSuccess('AI Commentary generated successfully!');
    } catch (error) {
      showError('Error generating commentary: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const parseCommentarySections = (text) => {
    if (!text) return {};
    
    const sections = {};
    const strengthsMatch = text.match(/(?:1\.\s*)?STRENGTHS[:\s]*([\s\S]*?)(?=(?:2\.\s*)?WEAKNESSES|$)/i);
    const weaknessesMatch = text.match(/(?:2\.\s*)?WEAKNESSES[:\s&]*([\s\S]*?)(?=(?:3\.\s*)?MANAGEMENT|$)/i);
    const managementMatch = text.match(/(?:3\.\s*)?MANAGEMENT COMMENTARY[:\s]*([\s\S]*?)(?=(?:4\.\s*)?RECOMMENDED|$)/i);
    const nextStepsMatch = text.match(/(?:4\.\s*)?RECOMMENDED NEXT STEPS[:\s]*([\s\S]*?)$/i);
    
    if (strengthsMatch) sections.strengths = strengthsMatch[1].trim();
    if (weaknessesMatch) sections.weaknesses = weaknessesMatch[1].trim();
    if (managementMatch) sections.management = managementMatch[1].trim();
    if (nextStepsMatch) sections.nextSteps = nextStepsMatch[1].trim();
    
    return sections;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading AI commentary...</p>
        </div>
      </div>
    );
  }

  const sections = commentary?.commentary ? parseCommentarySections(commentary.commentary) : {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold mb-1">AI Executive Commentary</h2>
              <p className="text-purple-100 text-sm">Powered by GPT-4o Analysis</p>
            </div>
          </div>
          <button
            onClick={handleGenerateCommentary}
            disabled={generating}
            className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 flex items-center gap-2 transition disabled:opacity-50"
          >
            {generating ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                {commentary?.commentary ? 'Regenerate' : 'Generate'} Commentary
              </>
            )}
          </button>
        </div>
      </div>

      {!commentary?.commentary ? (
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <Brain className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Commentary Generated Yet</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Generate an AI-powered executive analysis of your GRI reporting readiness. 
            Get insights on strengths, gaps, and actionable recommendations.
          </p>
          <button
            onClick={handleGenerateCommentary}
            disabled={generating}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-5 h-5" />
            Generate AI Commentary
          </button>
        </div>
      ) : (
        <>
          {/* Statistics Summary */}
          {commentary.statistics && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-indigo-600" />
                <h3 className="text-lg font-bold text-gray-900">Report Status at Time of Analysis</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-indigo-50 rounded-lg">
                  <div className="text-2xl font-bold text-indigo-700">{commentary.statistics.coverage_percentage}%</div>
                  <div className="text-xs text-indigo-600">Coverage</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">{commentary.statistics.complete_count}</div>
                  <div className="text-xs text-green-600">Complete</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-700">{commentary.statistics.partial_count || 0}</div>
                  <div className="text-xs text-yellow-600">Partial</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <div className="text-2xl font-bold text-red-700">{commentary.statistics.missing_count}</div>
                  <div className="text-xs text-red-600">Missing</div>
                </div>
              </div>
            </div>
          )}

          {/* Strengths Section */}
          {sections.strengths && (
            <div className="bg-white rounded-xl border-2 border-green-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-bold text-green-900">Strengths</h3>
              </div>
              <div className="prose prose-green max-w-none">
                <ReactMarkdown>{sections.strengths}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Weaknesses Section */}
          {sections.weaknesses && (
            <div className="bg-white rounded-xl border-2 border-orange-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle className="w-6 h-6 text-orange-600" />
                <h3 className="text-lg font-bold text-orange-900">Weaknesses & Gaps</h3>
              </div>
              <div className="prose prose-orange max-w-none">
                <ReactMarkdown>{sections.weaknesses}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Management Commentary Section */}
          {sections.management && (
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-6 h-6 text-indigo-600" />
                <h3 className="text-lg font-bold text-gray-900">Management Commentary</h3>
              </div>
              <div className="prose prose-indigo max-w-none text-gray-700">
                <ReactMarkdown>{sections.management}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Recommended Next Steps */}
          {sections.nextSteps && (
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Lightbulb className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-bold text-blue-900">Recommended Next Steps</h3>
              </div>
              <div className="prose prose-blue max-w-none">
                <ReactMarkdown>{sections.nextSteps}</ReactMarkdown>
              </div>
            </div>
          )}

          {/* Generation Timestamp */}
          {commentary.generated_at && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Generated on {new Date(commentary.generated_at).toLocaleString()}</span>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Brain className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-900">
                <p className="font-semibold mb-1">About AI Commentary</p>
                <p>
                  This analysis is generated by GPT-4o based on your current GRI disclosure status. 
                  It evaluates coverage completeness, identifies gaps, and provides strategic recommendations. 
                  Regenerate the commentary after making significant updates to get fresh insights.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GRIAICommentary;
